import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import moment from "moment";
import {
  Add,
  CancelOutlined,
  CheckCircleOutline,
  EditNoteOutlined,
  FileUploadOutlined,
  GroupsOutlined,
  KeyboardArrowDown,
  LayersOutlined,
  ViewQuiltOutlined,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { defaultAllSelection } from "services/constants";
import { Loader } from "components/Loader";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import { salaryRevisionListHeader } from "pages/PayRoll/PayRollHeaders";
import { useAuditActor } from "pages/PayRoll/PayInputs/Shared/auditHelpers";
import {
  GlobalBreadcrumb,
  GlobalConfirmDialog,
  sharedStickyFooterBarSx,
  sharedTableBodyCellSx,
  sharedTableHeadCellSx,
} from "components/DesignSystem/sharedUiPatterns";
import {
  APP_BAR_OFFSET,
  StatCard,
  designColors,
  designShell,
  designType,
  designWeights,
  withDesignFont,
} from "services/constants/designTokens";
import { ToneChip, useFilteredRows, useMeasuredHeight } from "../Shared/salaryUi";
import { currentTemplateVersion } from "../SalaryTemplates/salaryTemplateData";
import { getTemplateComponents, listSalaryTemplates, templateDetails } from "../SalaryTemplates/salaryTemplateApi";
import {
  emptyRevisionForm,
  employeeOfList,
  formatRupees,
  revisionToForm,
  rowComponents,
  rowNewGross,
  toRevisionRow,
} from "./revisionData";
import {
  deleteRevisionDraft,
  getRevision,
  getRevisionChoices,
  listEligibleEmployees,
  listRevisions,
  revisionAction,
  saveRevision,
  toRevisionBody,
} from "./revisionApi";
import { RevisionFormPanel, RevisionReviewPanel } from "./RevisionPanels";

// Payroll > Salary > Salary Revision: changes to employees who already
// have a salary, one request each, approved on submit for now (the L1 / L2
// approval comes later), on the payroll/salary/ revision APIs
// (revisionApi.js).

const ALL = "all";
const ACTION_BUTTON_WIDTH = 150;

// The cards the list API names. Revisions are approved on submit for now
// (no L1 / L2 wait), so there are no Pending cards.
const STATUS_CARDS = [
  { key: ALL, title: "All", detail: "Every revision request", icon: LayersOutlined, accent: designColors.tealDark, tint: designColors.tealTint },
  { key: "Draft", title: "Draft", detail: "Saved, not submitted", icon: EditNoteOutlined, accent: designColors.slate600, tint: designColors.slate100 },
  { key: "Approved", title: "Approved", detail: "Approved on submit · upcoming or applied", icon: CheckCircleOutline, accent: "#12B76A", tint: "#E7F8EF" },
  { key: "Rejected", title: "Rejected", detail: "Returned with remarks", icon: CancelOutlined, accent: "#DC2626", tint: "#FEF2F2" },
];

const ACTIONS = {
  Pending: { label: "Review", variant: "outlined" },
  Draft: { label: "Continue", variant: "primary" },
  Rejected: { label: "Edit & resubmit", variant: "danger" },
  Upcoming: { label: "View", variant: "outlined" },
  Applied: { label: "View", variant: "outlined" },
  Cancelled: { label: "View", variant: "outlined" },
};

const KIND_TONES = { "From template": "blue", Employees: "slate", Import: "purple" };

const sameName = (a, b) => Boolean(a && b) && String(a).split(" (")[0].trim().toLowerCase() === String(b).split(" (")[0].trim().toLowerCase();

export const SalaryRevisionScreen = (props) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh state per company.
  return <RevisionScreen key={`${domain}:${topbarCompanyId}`} {...props} />;
};

const RevisionScreen = ({ subModuleId }) => {
  const { domain, userScreenAccess, userDetails, topbarCompanyName, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const canEdit = checkEditorPermission(userScreenAccess?.module_details, "Salary Configuration", "Salary Revision");
  const validCompany =
    Number.isInteger(Number(topbarCompanyId)) && Number(topbarCompanyId) > 0 && Number(topbarCompanyId) !== defaultAllSelection;
  const actor = useAuditActor();

  const [revisions, setRevisions] = useState([]);
  const [serverCounts, setServerCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [catalog, setCatalog] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const [templateCache, setTemplateCache] = useState({});
  const [choices, setChoices] = useState({ reasons: null, minDate: "", lastProcessed: "" });
  const [eligible, setEligible] = useState({ list: [], loading: false, error: "", loaded: false });
  const [templateEmployees, setTemplateEmployees] = useState({});
  const requested = useRef({ eligible: false, templates: new Set(), templateEmployees: new Set() });

  const [statusCard, setStatusCard] = useState(ALL);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [reviewExpanded, setReviewExpanded] = useState(true);
  const [details, setDetails] = useState({ id: null, revision: null, loading: false });
  const [formPanel, setFormPanel] = useState({ open: false, revision: null, initial: null });
  // The revise form opens as the full view; the user can collapse it.
  const [formExpanded, setFormExpanded] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterRef, filterHeight] = useMeasuredHeight();

  const views = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "salary_revision_saved_views",
    fullHeaders: salaryRevisionListHeader,
    screenAccess: canEdit,
  });

  // ---- Loading ----

  useEffect(() => {
    if (!validCompany) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    listRevisions(domain, topbarCompanyId, controller.signal)
      .then((value) => {
        if (controller.signal.aborted) return;
        setRevisions(value.revisions);
        setServerCounts(value.counts);
        setLoading(false);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setRevisions([]);
        setLoading(false);
        showToast("error", error.message || "Couldn't load the salary revisions.");
      });
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany, refresh]);

  // Templates, components and choices: not critical, the list works
  // without them.
  useEffect(() => {
    if (!validCompany) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    listSalaryTemplates(domain, topbarCompanyId, signal).then((list) => !signal.aborted && setTemplateList(list)).catch(() => {});
    getTemplateComponents(domain, topbarCompanyId, signal).then((items) => !signal.aborted && setCatalog(items)).catch(() => {});
    getRevisionChoices(domain, signal).then((value) => !signal.aborted && setChoices(value)).catch(() => {});
    return () => controller.abort();
  }, [domain, topbarCompanyId, validCompany]);

  // Employees for the picker (once per form session; reloaded after a save).
  const ensureEligible = useCallback(() => {
    if (!validCompany || requested.current.eligible) return;
    requested.current.eligible = true;
    setEligible((current) => ({ ...current, loading: true, error: "" }));
    listEligibleEmployees(domain, topbarCompanyId)
      .then((list) => setEligible({ list, loading: false, error: "", loaded: true }))
      .catch((error) => {
        requested.current.eligible = false;
        setEligible({ list: [], loading: false, error: error.message || "Couldn't load the employees.", loaded: true });
      });
  }, [domain, topbarCompanyId, validCompany]);

  // Everyone on one template (From a template mode). Called every time a
  // template is picked, so the list is always fresh; the old list is
  // dropped first (the table shows Loading) and a call already running for
  // the same template isn't repeated.
  const ensureTemplateEmployees = useCallback(
    (templateId) => {
      const key = String(templateId ?? "");
      if (!validCompany || !key || requested.current.templateEmployees.has(key)) return;
      requested.current.templateEmployees.add(key);
      setTemplateEmployees((current) => {
        const next = { ...current };
        delete next[key];
        return next;
      });
      listEligibleEmployees(domain, topbarCompanyId, { templateId })
        .then((list) => setTemplateEmployees((current) => ({ ...current, [key]: list })))
        .catch((error) => {
          setTemplateEmployees((current) => ({ ...current, [key]: [] }));
          showToast("error", error.message || "Couldn't load the template's employees.");
        })
        .finally(() => requested.current.templateEmployees.delete(key));
    },
    [domain, topbarCompanyId, validCompany],
  );

  // One template's versions and rows (once).
  const ensureTemplate = useCallback(
    (templateId) => {
      const key = String(templateId ?? "");
      const listed = templateList.find((item) => String(item.id) === key);
      if (!key || !listed || requested.current.templates.has(key)) return;
      requested.current.templates.add(key);
      templateDetails(domain, listed)
        .then(({ template }) => setTemplateCache((current) => ({ ...current, [key]: template })))
        .catch(() => requested.current.templates.delete(key));
    },
    [domain, templateList],
  );

  const templates = useMemo(
    () => templateList.map((item) => (templateCache[String(item.id)] ? { ...item, ...templateCache[String(item.id)] } : item)),
    [templateList, templateCache],
  );

  // ---- List ----
  const allRows = useMemo(() => revisions.map((revision) => toRevisionRow(templates, revision)), [revisions, templates]);
  const counts = useMemo(
    () => ({
      [ALL]: serverCounts.all ?? allRows.length,
      Draft: serverCounts.Draft ?? allRows.filter((row) => row.card === "Draft").length,
      Approved: serverCounts.Approved ?? allRows.filter((row) => row.card === "Approved").length,
      Rejected: serverCounts.Rejected ?? allRows.filter((row) => row.card === "Rejected").length,
    }),
    [allRows, serverCounts],
  );
  const statusRows = useMemo(
    () => (statusCard === ALL ? allRows : allRows.filter((row) => row.card === statusCard)),
    [allRows, statusCard],
  );
  const [visibleRows, handleFilter] = useFilteredRows(statusRows);
  const reportMetadata = useMemo(
    () => ({ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }),
    [topbarCompanyName],
  );

  // ---- Review panel ----
  const listRevision = revisions.find((revision) => revision.id === reviewId) ?? null;
  const reviewRevision = details.revision && details.id === reviewId ? details.revision : listRevision;
  useEffect(() => {
    if (!listRevision) setReviewExpanded(true);
  }, [listRevision]);
  useEffect(() => {
    if (!reviewId) {
      setDetails({ id: null, revision: null, loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setDetails({ id: reviewId, revision: null, loading: true });
    getRevision(domain, reviewId, controller.signal)
      .then((revision) => {
        if (controller.signal.aborted) return;
        setDetails({ id: reviewId, revision: { ...listRevision, ...revision }, loading: false });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setDetails({ id: reviewId, revision: null, loading: false });
        showToast("error", error.message || "Couldn't load the revision.");
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, reviewId]);

  // The server decides in the end; these only hide buttons that can't work.
  const mineByName = (revision) => sameName(revision.created_by, actor.name);
  const canApproveRevision = (revision) =>
    canEdit && revision?.status === "Pending" && (revision.can_approve ?? (revision.pending_mine || !mineByName(revision)));
  const canCancelRevision = (revision) =>
    canEdit && revision?.status === "Pending" && (revision.can_cancel ?? (!revision.created_by || mineByName(revision)));

  // ---- Actions ----

  const runAction = async (action) => {
    if (busyRef.current || !validCompany || !canEdit) return { success: false };
    busyRef.current = true;
    setBusy(true);
    try {
      return { success: true, value: await action() };
    } catch (error) {
      showToast("error", error.message);
      return { success: false };
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  // A save or action changes who is eligible: the picker reloads next time.
  const reload = () => {
    requested.current.eligible = false;
    requested.current.templateEmployees = new Set();
    setEligible({ list: [], loading: false, error: "", loaded: false });
    setTemplateEmployees({});
    setRefresh((value) => value + 1);
  };

  const openForm = (revision, initial) => {
    setFormExpanded(true);
    setFormPanel({ open: true, revision, initial });
  };
  const closeForm = () => setFormPanel((current) => ({ ...current, open: false }));
  const startNew = (kind, ids = []) => openForm(null, emptyRevisionForm(kind, { ids, minDate: choices.minDate }));

  const openRevision = async (row) => {
    const revision = revisions.find((item) => item.id === row.id);
    if (!revision) return;
    if ((revision.status === "Draft" || revision.status === "Rejected") && canEdit && revision.kind !== "import" && revision.can_edit !== false) {
      const result = await runAction(() => getRevision(domain, revision.id));
      if (!result.success) return;
      const full = { ...revision, ...result.value };
      openForm(full, revisionToForm(full));
      return;
    }
    setReviewId(revision.id);
  };

  // Links from Employee Salary: ?revise=<employee ids> opens a revision for
  // those employees, ?review=<request no.> opens that request. The query is
  // cleared at once; the link is handled when the data is in.
  const { search, pathname } = useLocation();
  const routerHistory = useHistory();
  const [link, setLink] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(search);
    const revise = params.get("revise");
    const review = params.get("review");
    if (!revise && !review) return;
    setLink(review ? { review } : { revise: revise.split(",").filter(Boolean) });
    routerHistory.replace(pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  useEffect(() => {
    if (!link) return;
    if (link.review) {
      if (loading) return;
      const match = revisions.find((item) => item.number === link.review || item.id === link.review);
      if (match) setReviewId(match.id);
      else showToast("error", `Revision ${link.review} wasn't found.`);
      setLink(null);
      return;
    }
    // Revise: the form opens at once with those employees; their details
    // fill in when the employee list arrives, and anyone who can't be
    // revised shows the reason on their row.
    if (canEdit && link.revise.length) {
      ensureEligible();
      startNew("employees", link.revise);
    }
    setLink(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, loading, eligible.loaded]);

  // People the form can name: eligible employees + the request's own
  // (who come back ineligible because of this very request).
  const formPeople = useMemo(() => {
    const own = (formPanel.revision?.employees || []).map((row) => ({
      id: row.employee_id,
      name: row.name,
      code: row.code,
      department: row.department,
      group: row.group,
      template_id: row.from.template_id,
      template_name: row.from.template_name,
      version: row.from.version,
      gross: row.current_gross,
      customized: row.from.customized,
      lines: null,
      eligible: true,
      reason: "",
    }));
    const ids = new Set(own.map((person) => String(person.id)));
    return [...own, ...eligible.list.filter((person) => !ids.has(String(person.id)))];
  }, [eligible.list, formPanel.revision]);

  const bodyFor = (form, check, people, submit) => {
    const template = templates.find((item) => String(item.id) === String(form.template_id));
    const versions = template?.versions || [];
    const current = template ? currentTemplateVersion(template) : null;
    const target = form.kind === "template" ? versions.find((version) => version.version === form.target_version) || current : current;
    return toRevisionBody(form, {
      companyId: topbarCompanyId,
      rows: check.rows.map((row) => {
        const employee = employeeOfList(people, row.employee_id) || { gross: 0 };
        return {
          employee_id: row.employee_id,
          manual: row.manual,
          newGross: rowNewGross(form, employee, row),
          // The employee's own Customize edit, or their rows scaled to the
          // new gross, so the server saves what the preview shows.
          components: rowComponents(catalog, templates, form, employee, row),
        };
      }),
      targetVersionId: target?.version_id ?? null,
      sourceVersionIds: form.kind === "template" ? form.from_versions.map((number) => versions.find((version) => version.version === number)?.version_id) : [],
      baseLines: current?.lines || [],
      submit,
    });
  };

  const handleSaveDraft = async (form, check, people) => {
    const result = await runAction(() => saveRevision(domain, bodyFor(form, check, people, false)));
    if (!result.success) return;
    closeForm();
    reload();
    showToast("success", "Draft saved.");
  };

  // POST with submit, or PUT with submit (resubmits a Rejected one).
  const handleSubmit = async (form, check, people) => {
    const resubmit = formPanel.revision?.status === "Rejected";
    const result = await runAction(() => saveRevision(domain, bodyFor(form, check, people, true)));
    if (!result.success) return;
    closeForm();
    reload();
    showToast("success", `${resubmit ? "Resubmitted" : "Submitted"} and approved. New salaries take effect from the effective date.`);
  };

  const afterReview = (message) => {
    setReviewId(null);
    reload();
    showToast("success", message);
  };
  // One approval finishes a revision for now (no L1 / L2 wait).
  const handleApprove = async (revision, comment) => {
    const result = await runAction(() => revisionAction(domain, revision.id, "approve", comment));
    if (!result.success) return;
    afterReview("Approved. New salaries take effect from the effective date.");
  };
  const handleReject = async (revision, reason) => {
    const result = await runAction(() => revisionAction(domain, revision.id, "reject", reason));
    if (!result.success) return;
    afterReview("Revision rejected and returned to the submitter.");
  };
  const handleCancel = async (revision, remarks) => {
    const result = await runAction(() => revisionAction(domain, revision.id, "cancel", remarks));
    if (!result.success) return;
    afterReview("Revision cancelled.");
  };
  const handleDelete = async () => {
    const target = deleteTarget;
    const result = await runAction(() => deleteRevisionDraft(domain, target.id));
    if (!result.success) return;
    setDeleteTarget(null);
    closeForm();
    reload();
    showToast("success", "Draft deleted.");
  };

  // ---- Rendering ----
  const headCellSx = { ...sharedTableHeadCellSx, top: APP_BAR_OFFSET + filterHeight, whiteSpace: "nowrap" };
  const bodyCell = { ...sharedTableBodyCellSx, py: 1.25, whiteSpace: "normal" };

  const actionButton = (row) => {
    const revision = revisions.find((item) => item.id === row.id);
    const action = ACTIONS[row.status_key] || ACTIONS.Applied;
    const editable = canEdit && revision?.kind !== "import";
    const labelText = !editable && (row.status_key === "Draft" || row.status_key === "Rejected") ? "View" : action.label;
    // One width for every action button so the column lines up.
    const props = { disabled: busy, onClick: (event) => { event.stopPropagation(); openRevision(row); }, sx: { width: ACTION_BUTTON_WIDTH, minWidth: ACTION_BUTTON_WIDTH, minHeight: 36, px: 1.5, whiteSpace: "nowrap" } };
    if (action.variant === "primary" && labelText !== "View") return <PrimaryButton {...props}>{labelText}</PrimaryButton>;
    if (action.variant === "danger" && labelText !== "View") return <SecondaryButton {...props} sx={{ ...props.sx, color: "#B42318", borderColor: "#FCA5A5" }}>{labelText}</SecondaryButton>;
    return <SecondaryButton {...props}>{labelText}</SecondaryButton>;
  };

  const renderCell = (row, key) => {
    switch (key) {
      case "revision":
        return (
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{row.revision}</Mui.Typography>
            <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
              <ToneChip label={row.kind} tone={KIND_TONES[row.kind]} />
              <Mui.Typography sx={designType.captionText}>{row.detail}</Mui.Typography>
            </Mui.Stack>
          </Mui.Box>
        );
      case "change":
        return (
          <Mui.Box>
            <Mui.Typography sx={designType.tableBodyCell}>{row.change}</Mui.Typography>
            <Mui.Typography sx={designType.captionText}>{row.change_detail}</Mui.Typography>
          </Mui.Box>
        );
      case "gross": {
        const suffix = row.period !== "month" ? ` / ${row.period}` : "";
        return (
          <Mui.Box sx={{ textAlign: "right" }}>
            <Mui.Typography sx={designType.captionText}>{formatRupees(row.current)}{suffix}</Mui.Typography>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>{formatRupees(row.gross)}{suffix}</Mui.Typography>
          </Mui.Box>
        );
      }
      case "status":
        return (
          <Mui.Box>
            <ToneChip label={row.status} tone={row.status_tone} dot />
            {row.status_detail ? <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>{row.status_detail}</Mui.Typography> : null}
          </Mui.Box>
        );
      default:
        return row[key] || "–";
    }
  };

  const MENU = [
    { label: "Select employees", helper: "One or many, e.g. promotion or annual increment", icon: GroupsOutlined, onClick: () => startNew("employees") },
    { label: "From a template", helper: "Everyone on a template, e.g. move V3 to V4", icon: ViewQuiltOutlined, onClick: () => startNew("template") },
    { label: "Import from Excel", helper: "Not yet available", icon: FileUploadOutlined, disabled: true },
  ];
  const colSpan = (views.visibleHeaders?.length ?? 0) + 1;

  return (
    <ThemeProvider theme={withDesignFont}>
      <Mui.Box sx={{ ...designShell.pageSurface, pb: 2 }}>
        <Mui.Box sx={{ ...designShell.headerRowBase, alignItems: { xs: "flex-start", sm: "center" }, px: 2, pt: 2, mb: 2 }}>
          <Mui.Box sx={{ minWidth: 0 }}>
            <GlobalBreadcrumb trail={["Payroll", "Salary", "Salary Revision"]} />
            <Mui.Typography component="h1" sx={designType.pageTitle}>Salary Revision</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Change the salary of employees who already have one. Each revision creates a new salary version after approval.
            </Mui.Typography>
          </Mui.Box>
          {canEdit ? (
            <Mui.Button startIcon={<Add />} endIcon={<KeyboardArrowDown />} disabled={busy || loading || !validCompany} onClick={(event) => setMenuAnchor(event.currentTarget)} aria-haspopup="menu" aria-expanded={Boolean(menuAnchor)} sx={designShell.primaryButtonBase}>
              Revise Salary
            </Mui.Button>
          ) : null}
        </Mui.Box>
        <Mui.Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          elevation={0}
          PaperProps={{ sx: { ...designShell.actionMenuPaper, mt: 0.5, maxWidth: 380 } }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
        >
          {MENU.map((item) => (
            <Mui.MenuItem key={item.label} disabled={item.disabled} sx={{ ...designShell.actionMenuItem, alignItems: "flex-start", py: 1.25 }} onClick={() => { setMenuAnchor(null); item.onClick?.(); }}>
              <Mui.ListItemIcon sx={{ mt: 0.25 }}><item.icon fontSize="small" sx={{ color: designColors.tealDark }} /></Mui.ListItemIcon>
              <Mui.Box>
                <Mui.Typography sx={{ fontSize: 14, fontWeight: designWeights.semibold, color: designColors.slate900 }}>{item.label}</Mui.Typography>
                <Mui.Typography sx={{ ...designType.captionText, whiteSpace: "normal" }}>{item.helper}</Mui.Typography>
              </Mui.Box>
            </Mui.MenuItem>
          ))}
        </Mui.Menu>

        <Mui.Box sx={{ px: 2 }}>
          {!validCompany ? (
            <Mui.Typography sx={{ ...designType.helperText, mb: 2 }}>Select a company in the top bar to see its salary revisions.</Mui.Typography>
          ) : null}
          <Mui.Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1.5, mb: 2 }}>
            {STATUS_CARDS.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={loading ? "–" : counts[card.key]}
                detail={card.detail}
                accent={card.accent}
                tint={card.tint}
                onClick={() => setStatusCard(card.key)}
                isActive={statusCard === card.key}
                activeBorder
              />
            ))}
          </Mui.Box>

          <Mui.Box ref={filterRef} sx={{ position: "sticky", top: APP_BAR_OFFSET, zIndex: 4, backgroundColor: designColors.white }}>
            <CustomFilter
              title="Salary Revision"
              titleOverride={views.titleOverride}
              filterHeaders={views.visibleHeaders}
              data={statusRows}
              filteredData={visibleRows}
              onFilteredDataChange={handleFilter}
              screenAccess={canEdit}
              hideCount={true}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={reportMetadata}
              labeledToolbar={true}
              searchPlaceholder="Search employee, template or request no."
            />
          </Mui.Box>

          <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            <Mui.TableContainer sx={{ width: "100%", overflowX: "visible" }}>
              <ApproveTable stickyHeader>
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                    {views.visibleHeaders?.map((head) => (
                      <Mui.TableCell key={head.key} align={head.key === "gross" ? "right" : "left"} sx={headCellSx}>
                        {head.key === "gross" ? "Gross / month" : head.name}
                      </Mui.TableCell>
                    ))}
                    <Mui.TableCell align="right" sx={{ ...headCellSx, width: ACTION_BUTTON_WIDTH + 32 }}>Action</Mui.TableCell>
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {loading ? (
                    <Mui.TableRow>
                      <Mui.TableCell colSpan={colSpan} sx={{ borderBottom: "none" }}>
                        <Loader info="Loading..." minHeight="40vh" />
                      </Mui.TableCell>
                    </Mui.TableRow>
                  ) : visibleRows.length === 0 ? (
                    <TableEmptyState dataLength={statusRows} colSpan={colSpan} />
                  ) : (
                    visibleRows.map((row) => (
                      <Mui.TableRow key={row.id} hover onClick={() => !busy && openRevision(row)} sx={{ cursor: "pointer", ...designShell.tableRowHover, "&:last-child td": { borderBottom: "none" } }}>
                        {views.visibleHeaders.map((head) => (
                          <Mui.TableCell key={head.key} align={head.key === "gross" ? "right" : "left"} sx={bodyCell}>{renderCell(row, head.key)}</Mui.TableCell>
                        ))}
                        <Mui.TableCell align="right" sx={bodyCell}>{actionButton(row)}</Mui.TableCell>
                      </Mui.TableRow>
                    ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
            <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ ...sharedStickyFooterBarSx, ...designType.captionText, py: 1.25 }}>
              <span>Showing {visibleRows.length} of {statusRows.length} revision{statusRows.length === 1 ? "" : "s"}</span>
              <span>One open revision per employee · effective date after the last processed payroll</span>
            </Mui.Stack>
          </Mui.Box>
        </Mui.Box>
      </Mui.Box>

      <RevisionReviewPanel
        open={Boolean(reviewRevision)}
        revision={reviewRevision}
        catalog={catalog}
        templates={templates}
        canApprove={canApproveRevision(reviewRevision)}
        canCancel={canCancelRevision(reviewRevision)}
        loading={details.loading}
        busy={busy}
        expanded={Boolean(reviewRevision) && reviewExpanded}
        onToggleExpand={() => setReviewExpanded((value) => !value)}
        onClose={() => setReviewId(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
      />

      <RevisionFormPanel
        open={formPanel.open}
        initial={formPanel.initial}
        revision={formPanel.revision}
        catalog={catalog}
        templates={templates}
        employees={formPeople}
        templateEmployees={templateEmployees}
        eligibleLoading={eligible.loading}
        eligibleError={eligible.error}
        eligibleLoaded={eligible.loaded}
        reasons={choices.reasons}
        minDate={choices.minDate}
        lastProcessed={choices.lastProcessed}
        busy={busy}
        expanded={formPanel.open && formExpanded}
        onToggleExpand={() => setFormExpanded((value) => !value)}
        onClose={closeForm}
        onNeedEmployees={ensureEligible}
        onTemplateEmployees={ensureTemplateEmployees}
        onTemplate={ensureTemplate}
        onSaveDraft={handleSaveDraft}
        onSubmit={handleSubmit}
        onDelete={canEdit ? (revision) => setDeleteTarget(revision) : null}
      />

      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete draft"
        onCancel={() => { if (!busy) setDeleteTarget(null); }}
        onConfirm={handleDelete}
        confirmLabel="Delete"
        cancelLabel="Keep"
        loading={busy}
        loadingLabel="Deleting..."
        maxWidth="sm"
        danger
      >
        Delete this draft revision? The employees' current salaries stay as they are. This can't be undone.
      </GlobalConfirmDialog>
    </ThemeProvider>
  );
};
